import { Injectable } from '@angular/core';
import Bugsnag from '@bugsnag/browser';
import * as firebase from 'firebase/app';
import 'firebase/remote-config';

const MINIMUM_FETCH_INTERVAL_MILLIS = 60000; // 1 minute
const FETCH_TIMEOUT_MILLIS = 60000; // 1 minute

const REMOTE_CONFIG_KEYS = {
  app_em_manutencao: 'app_em_manutencao',
};

const DEFAULT_REMOTE_CONFIG = {
  [REMOTE_CONFIG_KEYS.app_em_manutencao]: false,
};

/**
 * Serviço responsável por gerenciar o Firebase Remote Config.
 * Controla a inicialização, busca e recuperação de valores de configuração remota.
 * 
 * @remarks
 * Este serviço utiliza o Firebase Remote Config para gerenciar feature flags e
 * configurações do aplicativo remotamente, sem necessidade de atualizações do app.
 * 
 * @example
 * ```typescript
 * constructor(private remoteConfig: RemoteConfigService) {}
 * 
 * async verificarManutencao() {
 *   const emManutencao = await this.remoteConfig.getMaintenanceMode();
 *   if (emManutencao) {
 *     // Tratar modo de manutenção
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class RemoteConfigService {
  private remoteConfig: firebase.remoteConfig.RemoteConfig;
  private initializationPromise: Promise<void>;
  private isInitialized = false;

  constructor() {
    this.remoteConfig = firebase.remoteConfig();
    this.initializationPromise = this.initializeRemoteConfig();
  }

  /**
   * Inicializa o Firebase Remote Config com configurações e valores padrão.
   * Define o intervalo mínimo de busca, timeout e ativa a configuração.
   * Valores padrão são definidos no objeto `DEFAULT_REMOTE_CONFIG`.
   * 
   * @throws {Error} Se a inicialização falhar
   * @private
   */
  private async initializeRemoteConfig() {
    if (this.isInitialized) {
      return;
    }

    try {
      this.remoteConfig.settings = {
        minimumFetchIntervalMillis: MINIMUM_FETCH_INTERVAL_MILLIS,
        fetchTimeoutMillis: FETCH_TIMEOUT_MILLIS,
      };

      this.remoteConfig.defaultConfig = DEFAULT_REMOTE_CONFIG;
      await this.remoteConfig.fetchAndActivate();
      this.isInitialized = true;
    } catch (error) {
      Bugsnag.notify(error, event => {
        event.context = 'Erro ao inicializar remote config';
      });
      throw error;
    }
  }

  /**
   * Verifica se o aplicativo está em modo de manutenção.
   * 
   * @returns Uma promise que resolve para `true` se o app estiver em manutenção, `false` caso contrário.
   * Se ocorrer um erro, retorna `false` e reporta o erro para o Bugsnag.
   * 
   * @example
   * ```typescript
   * const emManutencao = await remoteConfig.getMaintenanceMode();
   * if (emManutencao) {
   *   // Mostrar tela de manutenção
   * }
   * ```
   */
  public async getMaintenanceMode(): Promise<boolean> {
    try {
      await this.initializationPromise;
      const value = this.remoteConfig.getValue(REMOTE_CONFIG_KEYS.app_em_manutencao);
      return value.asBoolean();
    } catch (error) {
      Bugsnag.notify(error, event => {
        event.context = 'Erro ao obter flag de manutenção no remote config';
      });
      return false;
    }
  }

  /**
   * Força uma atualização dos valores de configuração remota.
   * Busca e ativa a configuração mais recente do Firebase Remote Config.
   * 
   * @returns Uma promise que resolve quando a configuração é atualizada.
   * Se ocorrer um erro, ele será registrado mas não será lançado.
   * 
   * @example
   * ```typescript
   * await remoteConfig.refreshConfig();
   * // Configuração agora está atualizada
   * ```
   */
  public async refreshConfig(): Promise<void> {
    try {
      await this.initializationPromise;
      await this.remoteConfig.fetchAndActivate();
    } catch (error) {
      console.error('Error refreshing remote config:', error);
    }
  }
}
